import React, { useState } from "react";

const BootstrapTable = ({
    headerColumns = [],
    gridData = [],
    footerContent = null,
    noAction = false,
    progressPending = false,
    pagination = true,
    paginationServer = false,
    paginationTotalRows = 0,
    paginationRowsPerPageOptionsEnabled = true,
    paginationRowsPerPageOptions = [5, 10, 25, 50, 100],
    currentPage = 1,
    perPage = 5,
    onChangePage,
    onChangeRowsPerPage,
    size = "sm",
    wrap = true,
    // New expandable rows props
    expandableRows = false,
    expandableRowsComponent = null,
    expandableRowDisabled = null,
    expandOnRowClicked = false,
    expandOnRowDoubleClicked = false,
}) => {

    const [expandedRows, setExpandedRows] = useState(new Set());

    // total number of rows (server provides full count when paginationServer)
    const totalRows = paginationServer ? (paginationTotalRows || 0) : gridData.length;
    const pageSize = perPage === 'All' ? totalRows || gridData.length : Number(perPage || 0) || 0;
    const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(totalRows / pageSize));
    const page = Math.min(Math.max(1, Number(currentPage || 1)), totalPages);

    // For server pagination, gridData usually already contains only the rows for the current page.
    // For client pagination, slice the gridData array.
    const visibleRows = paginationServer ? gridData : gridData.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);

    // Compute displayed start/end indices for "Showing X - Y of Z"
    const startIndex = totalRows === 0 ? 0 : (paginationServer ? (page - 1) * pageSize : (page - 1) * pageSize) ;
    const endIndex = totalRows === 0 ? 0 : (paginationServer ? (startIndex + visibleRows.length) : Math.min(totalRows, startIndex + visibleRows.length));
    // convert to 1-based display numbers
    const displayStart = startIndex === 0 ? (visibleRows.length > 0 ? 1 : 0) : startIndex + 1;
    const displayEnd = endIndex;

    const getCellValue = (col, row) => {
        if (typeof col.selector === "function") return col.selector(row);
        if (typeof col.selector === "string") return row[col.selector];
        return "";
    };

    // Expandable rows functions
    const getRowId = (row, index) => row.id ?? index;

    const isRowExpanded = (row, index) => {
        const rowId = getRowId(row, index);
        return expandedRows.has(rowId);
    };

    const isRowExpandable = (row) => {
        if (!expandableRows) return false;
        if (typeof expandableRowDisabled === 'function') {
            return !expandableRowDisabled(row);
        }
        return true;
    };

    const toggleRowExpansion = (row, index) => {
        if (!isRowExpandable(row)) return;
        
        const rowId = getRowId(row, index);
        const newExpandedRows = new Set(expandedRows);
        
        if (newExpandedRows.has(rowId)) {
            newExpandedRows.delete(rowId);
        } else {
            newExpandedRows.add(rowId);
        }
        
        setExpandedRows(newExpandedRows);
    };

    const handleRowClick = (row, index) => {
        if (expandOnRowClicked) {
            toggleRowExpansion(row, index);
        }
    };

    const handleRowDoubleClick = (row, index) => {
        if (expandOnRowDoubleClicked) {
            toggleRowExpansion(row, index);
        }
    };

    const handlePrev = () => {
        if (page > 1 && onChangePage) onChangePage(page - 1);
    };
    const handleNext = () => {
        if (page < totalPages && onChangePage) onChangePage(page + 1);
    };
    const handleFirst = () => {
        if (page > 1 && onChangePage) onChangePage(1);
    };
    const handleLast = () => {
        if (page < totalPages && onChangePage) onChangePage(totalPages);
    };
    const handlePerPageChange = (e) => {
        const v = e.target.value;
        const value = v === 'All' ? 'All' : Number(v);
        if (onChangeRowsPerPage) onChangeRowsPerPage(value);
    };

    // helper to convert numbers to px and pass strings through
    const toCssSize = (v) => {
        if (v == null) return undefined;
        return (typeof v === "number") ? `${v}px` : String(v);
    };

    // helper to compute style for column widths (supports width, minWidth/min, maxWidth/max)
    const getColStyle = (col) => {
        if (!col) return undefined;
        const style = {};
        const width = toCssSize(col.width);
        const minWidth = toCssSize(col.minWidth ?? col.min);
        const maxWidth = toCssSize(col.maxWidth ?? col.max);
        if (width) style.width = width;
        if (minWidth) style.minWidth = minWidth;
        if (maxWidth) style.maxWidth = maxWidth;
        return Object.keys(style).length ? style : undefined;
    };

    // helper to compute className for a column (respects col.className, col.left/right/center, and col.wrap)
    const getColClass = (col) => {
        if (!col) return undefined;

        const classSet = new Set();
        if (col.className) {
            col.className.split(/\s+/).filter(Boolean).forEach(c => classSet.add(c));
        }

        // only add alignment if none of the alignment classes already exist
        const alignmentClasses = ['text-left', 'text-right', 'text-center'];
        const hasAlignment = alignmentClasses.some(c => classSet.has(c));
        if (!hasAlignment) {
            if (col.left) classSet.add('text-left');
            else if (col.right) classSet.add('text-right');
            else if (col.center) classSet.add('text-center');
        }

        // If wrap is explicitly true, prevent wrapping
        if (col.wrap === true && !classSet.has('text-nowrap')) classSet.add('text-nowrap');

        return classSet.size ? Array.from(classSet).join(' ') : undefined;
    };

    const totalColumns = headerColumns.length + (noAction ? 0 : 1) + (expandableRows ? 1 : 0);

    return (
        <div>
            <div className="table-responsive">
                <table className={`table table-${size} table-bordered table-hover table-striped${wrap ? ' text-nowrap' : ''} mb-0`}>
                    <thead>
                        <tr>
                            {expandableRows && <th style={{ width: '50px' }} />}
                            {headerColumns.map((col, idx) => (
                                <th
                                    key={idx}
                                    style={getColStyle(col)}
                                    className={getColClass(col)}>
                                    {col.name}
                                </th>
                            ))}
                            {!noAction && <th />}
                        </tr>
                    </thead>
                    <tbody>
                        {progressPending ? (
                            <tr>
                                <td colSpan={totalColumns} className="text-center py-4">
                                    <i className="fa fa-spinner fa-lg spinner" /> Loading...
                                </td>
                            </tr>
                        ) : visibleRows.length > 0 ? (
                            visibleRows.map((row, rIdx) => {
                                const expanded = isRowExpanded(row, rIdx);
                                const expandable = isRowExpandable(row);
                                
                                return (
                                    <React.Fragment key={getRowId(row, rIdx)}>
                                        <tr 
                                            onClick={() => handleRowClick(row, rIdx)}
                                            onDoubleClick={() => handleRowDoubleClick(row, rIdx)}
                                            style={expandOnRowClicked || expandOnRowDoubleClicked ? { cursor: 'pointer' } : undefined}
                                        >
                                            {expandableRows && (
                                                <td className="text-center align-middle">
                                                    {expandable && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRowExpansion(row, rIdx);
                                                            }}
                                                            aria-label={expanded ? 'Collapse row' : 'Expand row'}
                                                        >
                                                            <i className={`fa fa-${expanded ? 'minus' : 'plus'}-circle`} />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                            {headerColumns.map((col, cIdx) => (
                                                <td
                                                    key={cIdx}
                                                    className={getColClass(col)}
                                                    style={getColStyle(col)}>
                                                    {getCellValue(col, row)}
                                                </td>
                                            ))}
                                            {!noAction && <td />}
                                        </tr>
                                        {expandableRows && expanded && expandableRowsComponent && (
                                            <tr>
                                                <td colSpan={totalColumns} className="p-0">
                                                    <div className="p-3 bg-white border-top">
                                                        {React.isValidElement(expandableRowsComponent) 
                                                            ? React.cloneElement(expandableRowsComponent, { data: row })
                                                            : typeof expandableRowsComponent === 'function'
                                                            ? expandableRowsComponent(row)
                                                            : expandableRowsComponent
                                                        }
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={totalColumns} className="text-muted font-italic">
                                    No data.
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {footerContent && 
                    <tfoot>
                        {footerContent}
                    </tfoot>
                    }
                </table>
            </div>

            {/* Pagination controls */}
            {pagination && totalRows > 0 && (
            <div className={`d-flex justify-content-between align-items-center mt-${(size == 'sm' ? '2' : '3')}`}>
                <div className="small text-muted">
                    Showing {displayStart} - {displayEnd} of {totalRows}
                </div>

                <div className="d-flex align-items-center">
                    {(paginationRowsPerPageOptionsEnabled) && (
                    <div className="mr-2">
                        <select className={`form-control form-control-${size}`} name="page_size" value={perPage} onChange={handlePerPageChange}>
                            {paginationRowsPerPageOptions && paginationRowsPerPageOptions.map((opt, i) => (
                                <option key={i} value={opt}>{opt}</option>
                            ))}
                            <option value="All">All</option>
                        </select>
                    </div>
                    )}

                    <div className={`btn-group btn-group-${size}`} role="group" aria-label="pagination">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={handleFirst}
                            disabled={page <= 1}
                            aria-label="first page"
                            title="First"
                        >
                            <i className="fa fa-angle-double-left" aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={handlePrev}
                            disabled={page <= 1}
                            aria-label="previous page"
                            title="Previous"
                        >
                            <i className="fa fa-angle-left" aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            className="btn btn-light"
                            disabled
                            aria-label="current page"
                            title={`Page ${page} of ${totalPages}`}
                        >
                            <i className="fa fa-circle" aria-hidden="true" /> <span className="mx-1">{page}</span> / {totalPages}
                        </button>

                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={handleNext}
                            disabled={page >= totalPages}
                            aria-label="next page"
                            title="Next"
                        >
                            <i className="fa fa-angle-right" aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={handleLast}
                            disabled={page >= totalPages}
                            aria-label="last page"
                            title="Last"
                        >
                            <i className="fa fa-angle-double-right" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
};

export default BootstrapTable;
